import { useReducer } from "react";
import { supabase } from "@/lib/supabase";
import {
	Button,
	Input,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/primitives";
import { UserPlus, CheckCircle, AlertCircle } from "lucide-react";
import { parseInvokeError } from "@/lib/errors";

// State types
interface State {
	email: string;
	userType: "mentor" | "mentee";
	isSubmitting: boolean;
	error: {
		title: string;
		message: string;
	} | null;
	success: string;
}

// Action types
type Action =
	| { type: "SET_EMAIL"; payload: string }
	| { type: "SET_USER_TYPE"; payload: "mentor" | "mentee" }
	| { type: "SUBMIT_START" }
	| { type: "SUBMIT_SUCCESS"; payload: string }
	| { type: "SUBMIT_ERROR"; payload: { title: string; message: string } }
	| { type: "RESET_FORM" };

const initialState: State = {
	email: "",
	userType: "mentee",
	isSubmitting: false,
	error: null,
	success: "",
};

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "SET_EMAIL":
			return { ...state, email: action.payload, error: null, success: "" };
		case "SET_USER_TYPE":
			return { ...state, userType: action.payload };
		case "SUBMIT_START":
			return { ...state, isSubmitting: true, error: null, success: "" };
		case "SUBMIT_SUCCESS":
			return {
				...state,
				isSubmitting: false,
				success: action.payload,
				email: "",
			};
		case "SUBMIT_ERROR":
			return {
				...state,
				isSubmitting: false,
				error: action.payload,
				email: state.email,
				userType: state.userType,
			};
		case "RESET_FORM":
			return initialState;
		default:
			return state;
	}
}

export function InviteUserPage() {
	const [state, dispatch] = useReducer(reducer, initialState);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		dispatch({ type: "SUBMIT_START" });

		const { data, error } = await supabase.functions.invoke("invite-user", {
			body: {
				email: state.email.trim(),
				user_type: state.userType,
			},
		});

		if (error) {
			const detailedError = await parseInvokeError(error as any);
			dispatch({
				type: "SUBMIT_ERROR",
				payload: {
					title: detailedError.error || "Failed to invite user",
					message:
						detailedError.details ||
						detailedError.error ||
						"Failed to invite user",
				},
			});
		} else {
			dispatch({
				type: "SUBMIT_SUCCESS",
				payload:
					data?.message ||
					`Successfully invited ${state.email} as a ${state.userType}`,
			});
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
			<Card className="w-full max-w-md shadow-xl border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
				<CardHeader className="text-center pb-2">
					<div className="mx-auto w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
						<UserPlus className="w-7 h-7 text-white" />
					</div>
					<CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
						Invite User
					</CardTitle>
					<CardDescription className="text-gray-600 dark:text-gray-400">
						Send an invitation to add a new user to the system
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="space-y-2">
							<Label htmlFor="email" className="text-sm font-medium">
								Email Address
							</Label>
							<Input
								id="email"
								type="email"
								value={state.email}
								onChange={(e) =>
									dispatch({ type: "SET_EMAIL", payload: e.target.value })
								}
								placeholder="user@example.com"
								required
								className="h-11"
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="userType" className="text-sm font-medium">
								User Type
							</Label>
							<Select
								value={state.userType}
								onValueChange={(v) =>
									dispatch({
										type: "SET_USER_TYPE",
										payload: v as "mentor" | "mentee",
									})
								}
							>
								<SelectTrigger className="h-11">
									<SelectValue placeholder="Select user type" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="mentee">
										<div className="flex items-center gap-2">
											<span className="w-2 h-2 rounded-full bg-blue-500" />
											Mentee
										</div>
									</SelectItem>
									<SelectItem value="mentor">
										<div className="flex items-center gap-2">
											<span className="w-2 h-2 rounded-full bg-purple-500" />
											Mentor
										</div>
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{state.error && (
							<div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
								<AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
								<div className="flex-1">
									<p className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">
										{state.error.title}
									</p>
									<p className="text-sm text-red-600 dark:text-red-400">
										{state.error.message}
									</p>
								</div>
							</div>
						)}

						{state.success && (
							<div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
								<CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
								<p className="text-sm text-green-700 dark:text-green-300">
									{state.success}
								</p>
							</div>
						)}

						<Button
							type="submit"
							disabled={state.isSubmitting || !state.email.trim()}
							className="w-full h-11 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
						>
							{state.isSubmitting ? (
								<>
									<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
									Sending Invitation...
								</>
							) : (
								<>
									<UserPlus className="w-4 h-4 mr-2" />
									Send Invitation
								</>
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
